import { defineMessages, useIntl } from 'react-intl';

import { SPACE_SETTINGS_SHAREABLE_LINKS } from '@confluence/named-routes/entry-points/routes.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';
import { useSpaceManagementOperations } from '@confluence/space-utils/entry-points/graphql.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';

import type {
	SpaceSettingsWebItemSectionsFragment_webItemSections,
	SpaceSettingsWebItemSectionsFragment_webItemSections_items,
} from './__types__/SpaceSettingsWebItemSectionsFragment.ts';
import {
	SPACE_PERMISSIONS_WEBITEM_SECTION_ID,
	ANONYMOUS_TAB_ID,
} from './useSpacePermissionsWebItems.tsx';
import { SPACE_PUBLIC_LINKS_WEBITEM_ID } from './usePublicLinksWebItem.tsx';
import type { SpaceSettingsWebItemSectionsInjector } from './SpaceSettingsWebItemSectionsInjector.ts';
import { SPACE_PERMISSIONS_COMPLETE_KEY } from './webItemCompleteKeys.ts';

export const SPACE_SHAREABLE_LINKS_WEBITEM_ID = 'space-settings-shareable-links';

const i18n = defineMessages({
	spacePermissionsWebItemShareableLinksLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.shared-links.non-final',
		defaultMessage: 'Access links',
		description: 'Label for the web item link for the shareable links settings page',
	},
});

const addShareableLinksWebItem = (
	shareableLinksWebItem: SpaceSettingsWebItemSectionsFragment_webItemSections_items,
	webItemSections:
		| ReadonlyArray<SpaceSettingsWebItemSectionsFragment_webItemSections | null>
		| undefined,
) => {
	return webItemSections?.map((section) => {
		if (section?.id === SPACE_PERMISSIONS_WEBITEM_SECTION_ID) {
			const publicLinksIndex = section.items.findIndex(
				(item) => item?.id === SPACE_PUBLIC_LINKS_WEBITEM_ID,
			);
			const anonymousIndex = section.items.findIndex((item) => item?.id === ANONYMOUS_TAB_ID);
			let anchorIndex: number;
			if (publicLinksIndex !== -1) {
				anchorIndex = publicLinksIndex;
			} else if (anonymousIndex !== -1) {
				anchorIndex = anonymousIndex;
			} else {
				anchorIndex = section.items.length - 1;
			}
			const insertAt = anchorIndex + 1;
			// XXX Don't mutate the input webItemSections! May be shared across invocations.
			const items = [
				...section.items.slice(0, insertAt),
				shareableLinksWebItem,
				...section.items.slice(insertAt),
			];
			return {
				...section,
				items,
			};
		}
		return section;
	});
};

export const useShareableLinksWebItem: SpaceSettingsWebItemSectionsInjector = ({
	webItemSections,
}) => {
	const intl = useIntl();
	const spaceKey = useSpaceKey();
	const { isRbacEnabled } = useSpaceRoleMode();
	const isRbacOrExperimentEnabled =
		isRbacEnabled || expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false);
	const { canManagePublicLinks } = useSpaceManagementOperations({
		spaceKey: spaceKey ?? '',
		skip: !spaceKey || !isRbacOrExperimentEnabled,
	});

	if (!spaceKey) {
		return webItemSections;
	}

	if (isRbacOrExperimentEnabled && !canManagePublicLinks) {
		return webItemSections;
	}

	if (!expValEquals('shareable_links_m0', 'isEnabled', true)) {
		return webItemSections;
	}

	const shareableLinksWebItem: SpaceSettingsWebItemSectionsFragment_webItemSections_items = {
		id: SPACE_SHAREABLE_LINKS_WEBITEM_ID,
		label: intl.formatMessage(i18n.spacePermissionsWebItemShareableLinksLink),
		url: SPACE_SETTINGS_SHAREABLE_LINKS.toUrl({ spaceKey }),
		completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + SPACE_SHAREABLE_LINKS_WEBITEM_ID,
		icon: null,
	};

	return addShareableLinksWebItem(shareableLinksWebItem, webItemSections);
};
